import React, { useState, useEffect } from 'react'; // useState, useEffect, useCallback
import styled from 'styled-components';
import gql from 'graphql-tag';
import { useStoreState } from 'easy-peasy';
import { Query } from 'react-apollo';
import { Label } from 'rbx';

import { Loading, Table, CheckBox } from '../../../components/elements';
import client from '../../../utils/apolloClient';
import useIntersectionObserver from '../../../utils/useIntersectionObserver';

const Container = styled.div`
  &&& {
    .modal-background {
      background-color: rgba(13, 13, 13, 0.28);
    }
    .modal-card-body {
      height: 100vh;
      padding: 0px;
    }
    .modal-card {
      max-height: calc(100vh - 20%);
      position: absolute;
      width: 60%;
    }
    .modal-card-head,
    .modal-card-foot {
      border-top-left-radius: 0px;
      border-top-right-radius: 0px;
    }
    .modal-card-title {
      font-size: 1.5rem;
      font-weight: 600;
    }
    .modal-card-head {
      border-bottom: 0px;
      padding: 1.5rem 1rem;
      background: white;
    }
    .modal-card-foot {
      justify-content: flex-end;
      border: 0px;
      padding: 1rem;
      background: white;
    }
    a {
      font-size: 1rem;
    }
  }
`;

const SecondaryHeader = styled.div`
  background: white;
`;
const InputWrapper = styled.div`
  padding: 1rem;
  width: 50%;
`;

const placesQuery = gql`
  query searchPlaces($input: SearchInput) {
    search_places(input: $input) {
      total_pages
      total_size
      place_listing {
        place_id
        status
        name
        city
        state
        country
      }
    }
  }
`;

const PlaceList = ({ place, selectedPlaces, setSelectedPlaces }) => {
  console.log('place list...');
  return (
    <React.Fragment>
      <td>
        <CheckBox
          value={selectedPlaces.has(place.place_id)}
          onChange={() => {
            if (selectedPlaces.has(place.place_id)) {
              setSelectedPlaces(prev => {
                const temp = new Map(prev);
                temp.delete(place.place_id);
                return temp;
              });
            } else {
              setSelectedPlaces(prev => {
                const temp = new Map(prev);
                temp.set(place.place_id, place);
                return temp;
              });
            }
          }}
        />
      </td>
      <td style={{ fontWeight: '600' }}>{place.name}</td>
      <td style={{ fontSize: '12px', color: '#737373' }}>{place.city}</td>
      <td style={{ fontWeight: '600' }}>{place.state}</td>
      <td
        style={{ fontSize: '12px', color: '#737373' }}
      >{`${place.status}`}</td>
    </React.Fragment>
  );
};

const listSize = 25;

const View = ({
  loading,
  // page,
  setPage,
  placeData,
  setPlaceData,
  search_places,
  selectedPlaces,
  setSelectedPlaces,
}) => {
  const { isIntersecting, ref } = useIntersectionObserver({
    threshold: 0.5,
  });

  const isIntersectingRef =
    search_places?.place_listing &&
    Array.isArray(search_places.place_listing) &&
    search_places.place_listing.length < listSize
      ? false
      : isIntersecting;

  useEffect(() => {
    setPlaceData(prev =>
      search_places.place_listing && Array.isArray(search_places.place_listing)
        ? [...prev, ...search_places.place_listing]
        : [...prev],
    );
  }, [search_places?.place_listing]);

  useEffect(() => {
    const onChange = prev => prev + 1;

    if (isIntersectingRef) {
      setPage(onChange);
    }
  }, [isIntersectingRef]);

  return (
    <tbody>
      {placeData.map(place => (
        <tr key={place.place_id}>
          <PlaceList
            place={place}
            selectedPlaces={selectedPlaces}
            setSelectedPlaces={setSelectedPlaces}
          />
        </tr>
      ))}
      {loading && (
        <div style={{ margin: '0 2rem' }}>
          <span style={{ color: '#00AEEF', fontSize: '24px' }}>Loading...</span>
        </div>
      )}
      <div ref={ref} className="load-more" style={{ marginBottom: '2rem' }} />
    </tbody>
  );
};

const truncateString = str =>
  str.length > 30 ? `${str.slice(0, 30)}...` : str;

const AssignPlacesModal = ({
  onSubmit,
  isActive,
  onClose,
  initialsItems,
  isSubmitting,
  allIsSelected,
}) => {
  const { userId } = useStoreState(state => state.auth);

  const [placeData, setPlaceData] = useState([]);
  const [placeName, setPlaceName] = useState('');
  const [page, setPage] = useState(1);
  const [selectedPlaces, setSelectedPlaces] = useState(new Map());
  const [allIsSelectedRef, setAllIsSelectedRef] = useState(() => allIsSelected);

  let selectedPlacesValue = Array.from(selectedPlaces.values()).map(ele => {
    const temp = truncateString(ele.name);
    return {
      value: temp,
      label: temp,
      place_id: ele.place_id,
    };
  });

  if (allIsSelectedRef) {
    selectedPlacesValue = [{ value: 'ALL', label: 'All' }];
  }

  const sort = 'UPDATED_DESC';
  const currentPage = parseInt(page, 10) - 1 >= 0 ? parseInt(page, 10) - 1 : 0;

  const placeNameFilter = placeName || '';

  const input = {
    user_id: userId,
    sort,
    from: currentPage * listSize,
    size: listSize,
  };

  const place_filter = {};

  Object.assign(
    place_filter,
    placeNameFilter && { place_name: placeNameFilter },
  );

  Object.assign(input, {
    filter: {
      place_filter,
    },
  });

  React.useEffect(() => {
    setSelectedPlaces(prev => new Map([...prev, ...initialsItems]));
  }, [initialsItems]);

  return (
    <Container className={`modal ${isActive && 'is-active'}`}>
      <div className="modal-background" />
      <div className="modal-card">
        <header className="modal-card-head">
          <p className="modal-card-title">Assign Place(s)</p>
          <button
            type="button"
            className="delete"
            aria-label="close"
            onClick={onClose}
          />
        </header>
        <SecondaryHeader>
          {selectedPlacesValue.length !== 0 && (
            <div
              style={{
                margin: '1rem',
                maxHeight: '100px',
                overflowY: 'scroll',
              }}
            >
              <Label>Selected Places</Label>
              <div style={{ width: '100%' }}>
                {selectedPlacesValue.map(ele => (
                  <button
                    className="button"
                    style={{
                      padding: '0.5rem',
                      backgroundColor: '#00b28c',
                      color: 'white',
                    }}
                    onClick={() => {
                      if (allIsSelectedRef) {
                        setAllIsSelectedRef(false);
                      } else {
                        setSelectedPlaces(prev => {
                          const temp = new Map(prev);
                          temp.delete(ele.place_id);
                          return temp;
                        });
                      }
                    }}
                  >
                    <span>{ele.value}</span>
                    <span className="icon is-small">
                      <i className="fas fa-times" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
          <InputWrapper>
            <div className="field">
              <p className="control has-icons-left has-icons-right">
                <input
                  className="input"
                  type="search"
                  placeholder="Search places..."
                  onChange={e => {
                    setPlaceData([]);
                    setPlaceName(e.target.value);
                    setPage(1);
                  }}
                  disabled={allIsSelectedRef}
                />
                <span className="icon is-small is-left">
                  <i className="fas fa-search" />
                </span>
              </p>
            </div>
          </InputWrapper>
        </SecondaryHeader>
        <section className="modal-card-body">
          <div style={{ margin: '1rem' }}>
            <CheckBox
              label="Assign All Places to the User"
              value={allIsSelectedRef}
              onChange={() => {
                setAllIsSelectedRef(!allIsSelectedRef);
              }}
            />
          </div>
          {!allIsSelectedRef && (
            <Table>
              <thead>
                <tr style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <th>Action</th>
                  <th>
                    <span>Place Name</span>
                  </th>
                  <th>
                    <span>City / Suburb</span>
                  </th>
                  <th>
                    <span>State</span>
                  </th>
                  <th>
                    <span>Status</span>
                  </th>
                </tr>
              </thead>
              <Query
                query={placesQuery}
                variables={{ input }}
                fetchPolicy="network-only"
                client={client.clientPrivate}
              >
                {({ data, loading, error }) => {
                  if (loading && page === 1) {
                    return <Loading />;
                  }

                  if (error) {
                    return 'error';
                  }

                  const { search_places } = data;

                  if (
                    placeData.length === 0 &&
                    (!search_places.place_listing ||
                      search_places.place_listing.length === 0)
                  ) {
                    return <div>NO RECORDS FOUND</div>;
                  }

                  return (
                    <React.Fragment>
                      <View
                        loading={loading}
                        page={page}
                        setPage={setPage}
                        placeData={placeData}
                        setPlaceData={setPlaceData}
                        search_places={search_places}
                        selectedPlaces={selectedPlaces}
                        setSelectedPlaces={setSelectedPlaces}
                      />
                    </React.Fragment>
                  );
                }}
              </Query>
            </Table>
          )}
        </section>
        <footer className="modal-card-foot">
          <button
            type="button"
            className="button is-grey-light"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="button"
            className={`button is-primary ${isSubmitting && 'is-loading'}`}
            onClick={async () => {
              onSubmit({
                places: selectedPlaces,
                allIsSelected: allIsSelectedRef,
              });
            }}
            // disabled={selectedItems.length === 0}
          >
            <p className="has-text-weight-semibold">Assign Place(s)</p>
          </button>
        </footer>
      </div>
    </Container>
  );
};

export default AssignPlacesModal;
